import { useTranslation } from "react-i18next"

import { RankPatch } from "@/components/rank/rank-patch"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"

/** RankPatch-mini inputs (OKS-512), or `null` when the ladder is empty. */
export interface PausedRankData {
  name: string
  /** 1-based ladder position. */
  position: number
  /** Total tiers in the brand ladder. */
  tierCount: number
}

export interface PausedRecordData {
  missionsCompleted: number
  views: number
  badgesCount: number
  streakDays: number
  rank: PausedRankData | null
  /** Pre-formatted, locale-aware active period string. */
  activePeriod: string
  /**
   * No-record edge (UX Spec §3): the member has no contribution yet. When
   * `true`, the stats grid + rank footer are hidden in favour of an honest
   * "Rookie · Joined {date}" line.
   */
  isRookie: boolean
  /** Pre-formatted joined date, shown in the rookie line. */
  joinedDate: string | null
}

interface PausedRecordCardProps {
  brandName: string
  record: PausedRecordData | null
  error: boolean
  /** Reloads the page to retry resolving the record (UX Spec §3 Error). */
  onRetry: () => void
}

/**
 * OKS-404 / OKS-561 — "YOUR RECORD WITH {BRAND}" preserved-record card
 * (UX Spec §2.3).
 *
 * Read-only — no links inside. OKS-561 evolved the figure set to a 2×2
 * grid of **Missions · Views · Badges · Streak** (numbers prominent,
 * labels muted) plus a footer row: the RankPatch mini (OKS-512) + "Final
 * rank: {rank} · Active {start}–{end}."
 *
 * Three shapes:
 *   - **Rookie / no-record edge** (`record.isRookie`): stats grid + rank
 *     footer hidden; an honest "Rookie · Joined {date}" line shows instead.
 *   - **Error** (`error` or `record == null`): inline message + Retry; the
 *     surrounding banner + identity + CTA stay usable so the creator is
 *     never locked out of reactivation/contact.
 *   - **Full record**: the 2×2 grid + rank footer.
 *
 * Every figure is server-scoped to the single current membership/brand
 * (TRD §6) — the card itself does no data fetching.
 */
export function PausedRecordCard({
  brandName,
  record,
  error,
  onRetry,
}: PausedRecordCardProps) {
  const { t } = useTranslation()

  return (
    <Card data-testid="paused-record-card">
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
            {t("crew_paused.record.title", { brand: brandName })}
          </h2>
          <p className="text-muted-foreground text-xs">
            {t("crew_paused.record.reassurance")}
          </p>
        </div>

        {error || record === null ? (
          <div
            className="flex flex-col items-start gap-3"
            data-testid="paused-record-error"
          >
            <p className="text-muted-foreground text-sm">
              {t("crew_paused.record.error")}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onRetry}
              data-testid="paused-record-retry"
            >
              {t("crew_paused.record.retry")}
            </Button>
          </div>
        ) : record.isRookie ? (
          <p
            className="text-foreground text-sm"
            data-testid="paused-rookie-line"
          >
            {record.joinedDate
              ? t("crew_paused.record.rookie", { date: record.joinedDate })
              : t("crew_paused.record.rookie_no_date")}
          </p>
        ) : (
          <>
            <dl className="grid grid-cols-2 gap-4">
              <StatCell
                testid="paused-stat-missions"
                label={t("crew_paused.record.missions")}
                value={String(record.missionsCompleted)}
              />
              <StatCell
                testid="paused-stat-views"
                label={t("crew_paused.record.views")}
                value={String(record.views)}
              />
              <StatCell
                testid="paused-stat-badges"
                label={t("crew_paused.record.badges")}
                value={String(record.badgesCount)}
              />
              <StatCell
                testid="paused-stat-streak"
                label={t("crew_paused.record.streak")}
                value={String(record.streakDays)}
              />
            </dl>

            <div
              className="border-border/60 flex items-center gap-3 border-t pt-3"
              data-testid="paused-record-footer"
            >
              {record.rank ? (
                <span data-testid="paused-rank-patch" className="shrink-0">
                  <RankPatch
                    tier={record.rank.position}
                    tierCount={record.rank.tierCount}
                    name={record.rank.name}
                    size="sm"
                  />
                </span>
              ) : null}
              <p className="text-muted-foreground text-xs">
                {record.rank
                  ? t("crew_paused.record.footer_rank_prefix", {
                      rank: record.rank.name,
                    })
                  : null}
                <span data-testid="paused-active-period">
                  {t("crew_paused.record.active_period", {
                    period: record.activePeriod,
                  })}
                </span>
              </p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

function StatCell({
  testid,
  label,
  value,
}: {
  testid: string
  label: string
  value: string
}) {
  return (
    <div className="flex flex-col gap-1" data-testid={testid}>
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="text-foreground text-lg font-semibold">{value}</dd>
    </div>
  )
}
